import { z } from 'zod'

const money = z
  .string()
  .trim()
  .regex(/^\d{1,10}(\.\d{1,2})?$/, 'Enter an amount like 150 or 150.50.')

export const categorySchema = z.object({
  name: z.string().trim().min(1, 'Enter a name.').max(100, 'Use at most 100 characters.'),
  isActive: z.boolean(),
})
export type CategoryValues = z.infer<typeof categorySchema>

export const feeItemSchema = z.object({
  categoryId: z.string().min(1, 'Choose a category.'),
  academicYearId: z.string().min(1, 'Choose an academic year.'),
  classId: z.string(),
  name: z.string().trim().min(1, 'Enter a name.').max(150, 'Use at most 150 characters.'),
  amount: money,
  frequency: z.enum(['monthly', 'termly', 'yearly', 'one_time'], { error: 'Choose how often it is billed.' }),
  dueDay: z
    .string()
    .trim()
    .refine(
      (v) => v === '' || (/^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= 28),
      'Use a day between 1 and 28.',
    ),
  isMandatory: z.boolean(),
  isActive: z.boolean(),
})
export type FeeItemValues = z.infer<typeof feeItemSchema>

export const discountSchema = z
  .object({
    name: z.string().trim().min(1, 'Enter a name.').max(100, 'Use at most 100 characters.'),
    type: z.enum(['percentage', 'fixed']),
    value: money,
    maxAmount: z.string().trim(),
    isActive: z.boolean(),
  })
  .refine((v) => v.type !== 'percentage' || Number(v.value) <= 100, {
    path: ['value'],
    message: 'A percentage cannot exceed 100.',
  })
  .refine((v) => Number(v.value) > 0, { path: ['value'], message: 'The discount must be greater than zero.' })
  .refine((v) => v.maxAmount === '' || /^\d{1,10}(\.\d{1,2})?$/.test(v.maxAmount), {
    path: ['maxAmount'],
    message: 'Enter an amount like 100 or 100.50.',
  })
export type DiscountValues = z.infer<typeof discountSchema>

/** Builds the payment schema for an invoice's outstanding balance (no overpayment). */
export function paymentSchema(
  outstanding: number,
  exceedMessage = `The amount cannot exceed the outstanding balance (${outstanding.toFixed(2)}).`,
) {
  return z.object({
    amount: money
      .refine((v) => Number(v) > 0, 'The amount must be greater than zero.')
      .refine((v) => Number(v) <= outstanding + 1e-9, exceedMessage),
    method: z.enum(['cash', 'bank_transfer', 'cheque', 'card', 'mobile_money'], {
      error: 'Choose a payment method.',
    }),
    paidOn: z.string().min(1, 'Choose the payment date.'),
    reference: z.string().trim().max(100, 'Use at most 100 characters.'),
    note: z.string().trim().max(255, 'Use at most 255 characters.'),
  })
}
export type PaymentValues = z.infer<ReturnType<typeof paymentSchema>>

export const reasonSchema = z.object({
  reason: z.string().trim().min(3, 'Give a short reason.').max(255, 'Use at most 255 characters.'),
})
export type ReasonValues = z.infer<typeof reasonSchema>

export const generateSchema = z
  .object({
    academicYearId: z.string().min(1, 'Choose an academic year.'),
    classId: z.string(),
    periodType: z.enum(['month', 'annual']),
    month: z.string(),
    issueDate: z.string(),
    dueDate: z.string(),
    includeOptional: z.boolean(),
  })
  .refine((v) => v.periodType === 'annual' || /^\d{4}-(0[1-9]|1[0-2])$/.test(v.month), {
    path: ['month'],
    message: 'Choose the month to bill.',
  })
  .refine((v) => !v.dueDate || !v.issueDate || v.dueDate >= v.issueDate, {
    path: ['dueDate'],
    message: 'The due date cannot be before the issue date.',
  })
export type GenerateValues = z.infer<typeof generateSchema>
